import { useState, useEffect, useCallback, useRef } from 'react';
import priceService from '../services/priceService';
import publicPriceService from '../services/publicPriceService';
import iconService from '../services/iconService';
import { useAuth } from '../contexts/AuthContext';

export const usePrices = (symbols = [], autoRefresh = true, caller = 'unknown') => {
  const { currentUser } = useAuth();
  const isLoggedIn = !!currentUser;
  const [prices, setPrices] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [freshnessInfo, setFreshnessInfo] = useState(null);
  const iconsLoadedRef = useRef(false);

  const fetchPrices = useCallback(async (loadIcons = true) => {
    // console.log(`[usePrices] fetchPrices called with ${symbols?.length || 0} symbols from ${caller}:`, symbols);
    
    if (!symbols || symbols.length === 0) {
      // console.log(`[usePrices] No symbols provided, clearing prices`);
      setPrices({});
      setLastUpdate(null);
      setFreshnessInfo(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // console.log(`[usePrices] Fetching prices for ${symbols.length} symbols from ${caller}`);
      
      // Use appropriate price service based on authentication status
      const currentPriceService = isLoggedIn ? priceService : publicPriceService;
      const priceData = await currentPriceService.fetchPrices(symbols, `${caller}${isLoggedIn ? '' : '-public'}`);
      setPrices(priceData);
      setLastUpdate(currentPriceService.getLastFetchTime());
      
      // Get freshness information (now async)
      const freshness = await currentPriceService.getFreshnessSummary(symbols);
      setFreshnessInfo(freshness);
      
      // Load icons ONLY on initial load, not on every price refresh, and only for authenticated users
      const symbolsWithPrices = Object.keys(priceData);
      if (loadIcons && symbolsWithPrices.length > 0 && !iconsLoadedRef.current && isLoggedIn) {
        // console.log(`[usePrices] Loading icons for ${symbolsWithPrices.length} symbols with prices (initial load)`);
        iconsLoadedRef.current = true;
        // Load icons in the background (don't await to keep price fetching fast)
        iconService.loadAllIcons().catch(err => {
          console.warn('[usePrices] Error loading icons:', err);
        });
      }
      
      // console.log(`[usePrices] Successfully fetched prices for ${Object.keys(priceData).length} symbols from ${caller}`);
    } catch (err) {
      console.error(`[usePrices] Error fetching prices from ${caller}:`, err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [symbols, caller, isLoggedIn]);

  // Initial fetch with icons
  useEffect(() => {
    fetchPrices(true); // Load icons on initial fetch
  }, [fetchPrices]);

  // Auto-refresh every 60 seconds if enabled (without loading icons)
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchPrices(false); // Don't load icons on price refresh
    }, 60 * 1000); // 60 seconds - match server update frequency

    return () => clearInterval(interval);
  }, [fetchPrices, autoRefresh]);

  // Manual refresh function
  const refresh = useCallback(() => {
    fetchPrices(false); // Don't load icons on manual refresh
  }, [fetchPrices]);

  // Force refresh with icons (for when new coins are discovered)
  const refreshWithIcons = useCallback(() => {
    iconsLoadedRef.current = false; // Reset flag to allow icon loading
    fetchPrices(true);
  }, [fetchPrices]);

  // Force refresh by clearing cache and fetching fresh data
  const forceRefresh = useCallback(async () => {
    // console.log('[usePrices] Force refreshing prices');
    setLoading(true);
    setError(null);
    try {
      const priceData = await priceService.forceRefreshPrices(symbols, caller);
      setPrices(priceData);
      setLastUpdate(priceService.getLastFetchTime());
      
      // Get freshness information
      const freshness = await priceService.getFreshnessSummary(symbols);
      setFreshnessInfo(freshness);
      
      // console.log(`[usePrices] Force refresh completed: ${Object.keys(priceData).length} prices`);
    } catch (err) {
      console.error(`[usePrices] Force refresh error:`, err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [symbols, caller]);

  // Get freshness information
  const getFreshnessInfo = useCallback(() => {
    return freshnessInfo;
  }, [freshnessInfo]);

  // Get per-coin update times
  const getPerCoinUpdateTimes = useCallback(() => {
    return priceService.getPerCoinUpdateTimes();
  }, []);

  // Check if currently refreshing
  const isRefreshing = useCallback(() => {
    return priceService.isRefreshingPrices();
  }, []);

  return {
    prices,
    loading,
    error,
    lastUpdate,
    freshnessInfo,
    refresh,
    refreshWithIcons,
    forceRefresh,
    getFreshnessInfo,
    getPerCoinUpdateTimes,
    isRefreshing: isRefreshing()
  };
}; 